import { Checkout } from '@vp/common';
import * as Sentry from '@sentry/vue';
import { CustomerInformation } from '../useOrderDetails.ts';
import { isRef, ref, Ref } from 'vue';
import { until } from '@vueuse/core';
import type { HiveSdkStub } from '../types/global';

type HiveSubscribeDetails = {
  email?: string;
  didOptIn?: boolean;
  phoneNumber?: string;
  didSmsOptIn?: boolean;

  firstName?: string;
  lastName?: string;

  zipCode?: string;
  country?: string;
  city?: string;
  state?: string;
  mailingAddress?: string;
};

export const useHive = (brandIdRaw: string | Ref<string | undefined>) => {
  if (typeof window === 'undefined') {
    throw new Error('window must be defined');
  }

  const brandId = isRef(brandIdRaw) ? brandIdRaw : ref(brandIdRaw);

  const initHive = (brandId: string) => {
    (function (h: { HiveSDKObject: string; HIVE_SDK: HiveSdkStub }, i, v: 'script', e, s: 'HIVE_SDK') {
      h.HiveSDKObject = s;
      h[s] = h[s] || function () {
        // eslint-disable-next-line prefer-rest-params
        (h[s].q = h[s].q || []).push(arguments);
      };

      const d = i.createElement(v);
      d.async = true;
      d.id = s;
      d.src = e + '?r=' + Math.floor(Date.now() / 60000);

      // A document with no attached <script> leaves the queue stub installed and the CDN
      // script unfetched. Analytics must never be able to take the page down with it.
      const k = i.getElementsByTagName(v)[0];
      k.parentNode?.insertBefore(d, k);
    })(window, document, 'script', 'https://cdn-prod.hive.co/static/js/sdk-loader.js', 'HIVE_SDK');

    HIVE_SDK('init', brandId);
  };

  // The loader assigns `HIVE_SDK` synchronously, before it even requests the CDN script, so a
  // blocked or failed load still leaves a callable queue-push stub — those calls queue and are
  // dropped, they never throw. Two cases do throw, and this covers both: an account whose brand
  // id is withheld never runs the loader at all, and a blocker's surrogate can define the
  // symbol as something that is not callable. Hence callability rather than presence alone.
  const isHiveAvailable = () => 'HIVE_SDK' in window && typeof window.HIVE_SDK === 'function';

  const subscribeCustomerToHive = (details: HiveSubscribeDetails) => {
    if (!isHiveAvailable()) {
      return;
    }

    HIVE_SDK('emailSignup', details);
  };

  const subscribeOrderCustomer = (customerInformation: CustomerInformation) => {
    try {
      subscribeCustomerToHive({
        ...customerInformation.customer,
        zipCode: customerInformation.address?.postalCode,
        country: customerInformation.address?.country,
        city: customerInformation.address?.city,
        state: customerInformation.address?.state,
        mailingAddress: customerInformation.address?.street1,
        didOptIn: (customerInformation.agreements?.find(
          ({ key }: { key: string }) => key == 'newsletter',
        ) || {})['value'],
        didSmsOptIn: (customerInformation.agreements?.find(
          ({ key }: { key: string }) => key == 'sms_newsletter',
        ) || {})['value'],
      });
    } catch (e) {
      Sentry.captureException(e);
    }
  };

  // `publicOrderId` replaces what used to be `orderSummary.fingerprint.slice(-10)`. Ten
  // characters of a credential is still a credential: it left a brand's Hive account holding
  // most of a key to its buyers' personal data on the public checkout API. The id shape
  // changes from a fingerprint suffix to a digest, so orders created either side of this
  // change look different in a venue's Hive account — harmless, because `ticketingOrder
  // create` is one-shot, nothing updates a Hive order later and no code reads the id back.
  // Unverified against a live brand account: `HIVE_SDK` is a fire-and-forget queue push with
  // no response, so if 32 characters were rejected server-side it would fail invisibly.
  const confirmOrder = (
    orderSummary: Checkout.OrderSummary.OrderSummary,
    eventUrl: string,
    publicOrderId: string | undefined,
  ) => {
    // No id, no record. An unidentified ticketing order cannot be reconciled with anything,
    // which is worse than the order simply not reaching Hive. Effectively unreachable: either
    // an insecure context, where the payment would have failed first, or a digest failure,
    // which `publicOrderId` reports to Sentry — so a record skipped here is not silent.
    if (!publicOrderId) {
      return;
    }

    // No SDK on the page means nothing to record against and no error worth reporting.
    if (!isHiveAvailable()) {
      return;
    }

    const { event, breakdown } = orderSummary;
    if (!event || !breakdown) {
      throw new Error('confirmOrder: order summary is missing its event or breakdown');
    }

    const artists = event.artists?.map(artist => artist).join(', ');

    // TODO: eventUrl is temporary, and should be obtained from event object
    HIVE_SDK(
      'ticketingOrder',
      'create',
      {
        id: publicOrderId,
        status: 'completed',
        total_paid: breakdown.orderTotal,

        event: {
          id: event.id,
          name: event.name,
          description: event.description,
          start_time: event.startTime,
          end_time: event.endTime,
          url: eventUrl,
          image_url: event.images?.[0]?.variants?.[0]?.src,

          artist_names: artists,
          // artist_names: formatHiveParam($('#hive_event_artists')),
          // tag_names: formatHiveParam($('#hive_event_tags')),
          // genre_names: formatHiveParam($('#hive_event_tags')),
        },

        venue: {
          name: event.venue?.name,
          city: event.venue?.city,
          state: event.venue?.state,
          country: event.venue?.country,
        },
      },
    );
  };

  (async () => {
    await until(brandId).toBeTruthy();
    initHive(brandId.value!);
  })();

  return {
    initHive,
    subscribeCustomerToHive,
    subscribeOrderCustomer,
    confirmOrder,
  };
};
